/**
 * ZIndex - Centralized z-index scale for consistent layering
 * 
 * This file defines the z-index hierarchy for all overlays, modals, and floating elements.
 * Always use these constants instead of magic numbers to maintain consistency.
 */

/**
 * Z-index scale for the application
 * Each level is clearly separated to prevent collisions
 */
export const ZIndex = {
  /** Base content - default stacking context (0-40) */
  BASE: 0,
  
  /** Dropdowns and tooltips within content */
  DROPDOWN: 10,
  
  /** Sticky headers, navigation elements */
  STICKY: 20,
  
  /** Fixed navigation, sidebar (when overlaying) */
  NAVIGATION: 50,
  
  /** Fullscreen overlays (login, lock screen) */
  OVERLAY: 9999,
  
  /** Modal dialogs - above overlays */
  MODAL: 10000,
  
  /** Dropdowns inside modals - above modals but below toasts */
  MODAL_DROPDOWN: 10001,
  
  /** Toast notifications - topmost layer */
  TOAST: 10002,
  
  /** Critical alerts that must be visible above everything */
  CRITICAL: 10003
} as const;

/**
 * Type for z-index values
 */
export type ZIndexValue = typeof ZIndex[keyof typeof ZIndex];

/**
 * Get z-index as inline style string
 * @param level The z-index level from ZIndex constants
 * @returns Style string for inline use
 */
export function getZIndexStyle(level: ZIndexValue): string {
  return `z-index: ${level}`;
}

/**
 * Get z-index as Tailwind-compatible class
 * Note: Only works for values that match Tailwind's z-* classes (0-50)
 * For higher values, use inline styles
 * @param level The z-index level from ZIndex constants
 * @returns Tailwind class or empty string
 */
export function getZIndexClass(level: ZIndexValue): string {
  if (level <= 50) {
    return `z-${level}`;
  }
  // For values > 50, Tailwind requires arbitrary values
  return `z-[${level}]`;
}
